/**
 * Client de la page publique de partage — F-CNF-07, F-CNF-08 (SA-16).
 *
 * Requête en CORS direct vers l'API, sans jeton ni compte : quiconque tient
 * le lien n'a ni l'un ni l'autre. `ORIGINE_PARTAGE_PUBLIC`, côté serveur, est
 * ce qui borne qui peut appeler `GET /partage/:pays/:jeton` — cette page n'a
 * donc rien de plus à prouver d'elle-même.
 */

export interface PositionGeo {
  readonly latitude: number;
  readonly longitude: number;
}

/**
 * Ce que la page publique reçoit : la position du véhicule et l'estimation
 * d'arrivée, rien d'autre (décision du porteur du 22 octobre 2026, SA-16).
 * Miroir de `PartagePublic` (services/api/src/modules/confiance/partage.ts).
 */
export interface PartagePublic {
  readonly position: PositionGeo | null;
  readonly arriveeEstimeeLe: Date | null;
  readonly sondageRepliSecondes: number;
}

export type ResultatPartage =
  | { readonly type: 'trouve'; readonly partage: PartagePublic }
  | { readonly type: 'introuvable' }
  | { readonly type: 'erreur' };

/** Cadence de repli tant qu'aucune réponse n'a renseigné la vraie valeur, ou si elle est illisible. */
export const SECONDES_SONDAGE_PAR_DEFAUT = 8;

function nombreFini(valeur: unknown): valeur is number {
  return typeof valeur === 'number' && Number.isFinite(valeur);
}

/**
 * Retient les trois champs attendus et rien d'autre.
 *
 * Pas de `...brut` : un champ en trop dans la réponse (une API plus ancienne qui
 * rendait encore la destination, le chauffeur ou le véhicule, ou une évolution
 * future) ne doit pas atterrir dans l'état de la page, où un composant pourrait
 * l'afficher sans que personne ne l'ait décidé.
 */
function analyser(corps: unknown): PartagePublic {
  const brut = (typeof corps === 'object' && corps !== null ? corps : {}) as Record<
    string,
    unknown
  >;
  const position = brut['position'] as Partial<PositionGeo> | null | undefined;
  const arrivee =
    typeof brut['arriveeEstimeeLe'] === 'string' ? new Date(brut['arriveeEstimeeLe']) : null;
  const sondage = brut['sondageRepliSecondes'];
  return {
    position:
      position !== null &&
      position !== undefined &&
      nombreFini(position.latitude) &&
      nombreFini(position.longitude)
        ? { latitude: position.latitude, longitude: position.longitude }
        : null,
    arriveeEstimeeLe: arrivee !== null && !Number.isNaN(arrivee.getTime()) ? arrivee : null,
    sondageRepliSecondes:
      nombreFini(sondage) && sondage > 0 ? sondage : SECONDES_SONDAGE_PAR_DEFAUT,
  };
}

/** Large pour une 3G chargée, fini pour que le sondage ne reste jamais suspendu. */
const TIMEOUT_REQUETE_MS = 20_000;

/**
 * Relit l'état du trajet partagé. Ne lève jamais : une panne réseau ou un
 * lien fermé sont des résultats de ce domaine, pas des exceptions — la page
 * doit pouvoir les distinguer sans bloc `try/catch` à chaque appel.
 */
export async function lirePartage(
  origine: string,
  pays: string,
  jeton: string,
  signal?: AbortSignal,
): Promise<ResultatPartage> {
  // Sans délai, une requête muette figerait le sondage : le suivant n'est
  // programmé qu'à la réponse. `AbortSignal.any` manque aux navigateurs Android
  // anciens, d'où le relais manuel du signal de la page. Le minuteur court
  // jusqu'à la fin de la lecture du corps.
  const controle = new AbortController();
  const minuterie = setTimeout(() => controle.abort(), TIMEOUT_REQUETE_MS);
  if (signal?.aborted === true) controle.abort();
  else signal?.addEventListener('abort', () => controle.abort(), { once: true });

  try {
    const reponse = await fetch(`${origine}/partage/${pays}/${jeton}`, {
      signal: controle.signal,
    });
    if (reponse.status === 404) return { type: 'introuvable' };
    if (!reponse.ok) return { type: 'erreur' };
    return { type: 'trouve', partage: analyser(await reponse.json()) };
  } catch {
    return { type: 'erreur' };
  } finally {
    clearTimeout(minuterie);
  }
}
